import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
// Imported from the module, not the package barrel: the barrel re-exports this
// step, and going through it would close a cycle.
import {
  DEFAULT_FAN_IN_MODE,
  FAN_IN_MODES,
} from "../../utils/dynamicSockets/fanInOutputSockets";

/**
 * Fan In ("Await") Step
 *
 * The join half of fanout. Today fanout is fork-the-whole-tail-and-never-rejoin:
 * the parent clones every downstream step into N children and never collects
 * their outputs back. `fan_in` is the barrier that ends a fanned region and
 * hands the parent workflow the children's values.
 *
 * It is a **step**, not an expression form, because it changes how execution is
 * scheduled rather than what a value is — the same reason the split side
 * (Decision, Confidence Threshold, Switch) is modelled as step types. See
 * `docs/workflow-closures-design.md` § "`fan_in` — the gather / join step".
 *
 * ```
 * Extract ──[array<doc>]──▶ Classify ──▶ Enrich ──▶ Await ──▶ Report
 *                          └────── fanned region ─────┘
 * ```
 *
 * Three properties make this step unlike every other one:
 *
 * 1. **It never runs as an activity.** The orchestrator resolves it at the join
 *    site, where all N child outputs exist simultaneously — not through the
 *    normal readiness queue, and not via a handler. The registered handler
 *    exists only to fail loudly if a `fan_in` is ever reached on the ordinary
 *    path (which means its topology was invalid).
 * 2. **It is exempt from fanout detection.** `fan_in` is the *un*-fanout; it
 *    must never be fanned item-by-item.
 * 3. **Its output element type comes from the wire, not from config.** It is
 *    generic (`input<T> → array<T>`) and monomorphized when the input is wired.
 *
 * No `batch` config: the step does zero per-item work (a barrier plus an
 * assemble in the orchestrator), so activity-packing is meaningless here.
 */
export const fanInStep = defineStepConfig({
  type: "fan_in",
  name: "Await",
  description:
    "Barrier that ends a fanned-out region and gathers the children's outputs back into the parent workflow",
  category: "Control Flow",

  experimental: {
    reason:
      "The join half of fanout is new and its topology rules are still settling: which regions can be joined, and what a batched or detached fanout does underneath one, have both moved during review. Publish-time validation refuses the shapes known not to work, but expect the accepted set to keep narrowing.",
  },

  // Input socket is regenerated dynamically so it can be retyped when the
  // canvas monomorphizes `T`; the schema here is the untyped shape.
  inputSchema: z.object({
    value: z
      .any()
      .describe(
        "The per-child value to gather. Must be fed from inside exactly one fanned-out region.",
      ),
  }),

  // Output sockets are generated dynamically from `config.mode` plus the
  // resolved type of the wire feeding `value`. `z.record()` so the mode-
  // dependent keys (`results` vs `result`, optional `error`) survive Zod
  // validation — `z.object()` would strip them. Same trick switchStep uses.
  outputSchema: z.record(z.string(), z.any()),

  configSchema: z.object({
    mode: z
      .enum(FAN_IN_MODES)
      .default(DEFAULT_FAN_IN_MODE)
      .describe(
        "How the barrier resolves: all_success (wait for all, any failure routes to error), all_settled (wait for all, keep partial results), first_success (first fulfilled wins, cancel the rest), first_settled (first to settle wins, cancel the rest)",
      ),
  }),

  emits: [] as const,
});
